import type { BasicFilterCondition, FilterCondition } from '../types/filters';

export interface GridSetFilterModel {
  filterType: 'set' | 'agSetColumnFilter';
  values: unknown[];
  colId?: string;
  /**
   * REG-800: the checkbox filter can start from "everything ticked" and untick
   * a few, without loading every choice of a large server-backed column. Then
   * `values` lists the unticked choices. A non-excluded model with no values
   * is "Deselect all": nothing passes.
   */
  excluded?: boolean;
}

export type SetFilterPlan =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'include'; values: unknown[]; includesNull: boolean }
  | { kind: 'exclude'; values: unknown[]; excludesNull: boolean };

/** What a set filter model asks the query layer for, with NULL split out. */
export function planSetFilter(model: GridSetFilterModel): SetFilterPlan {
  const values = Array.isArray(model.values) ? model.values : [];
  const nonNull = values.filter((value) => value !== null && value !== undefined);
  const hasNull = nonNull.length !== values.length;
  if (model.excluded) {
    if (values.length === 0) return { kind: 'all' };
    return { kind: 'exclude', values: nonNull, excludesNull: hasNull };
  }
  if (values.length === 0) return { kind: 'none' };
  return { kind: 'include', values: nonNull, includesNull: hasNull };
}

export interface GridMultiFilterModel {
  filterType: 'multi';
  filterModels: Array<Record<string, unknown> | null> | null;
}

export function isSetFilterModel(filter: unknown): filter is GridSetFilterModel {
  if (!filter || typeof filter !== 'object') return false;
  const filterType = (filter as { filterType?: unknown }).filterType;
  return filterType === 'set' || filterType === 'agSetColumnFilter';
}

/**
 * The server-side grid condition for a set filter, or null when it filters
 * nothing. NOT IN drops NULL rows on its own, so blanks are kept explicitly
 * unless they were unticked.
 */
export function setFilterQueryCondition(
  model: GridSetFilterModel,
  filterType: BasicFilterCondition['filterType'],
  colId: string,
): FilterCondition | null {
  const plan = planSetFilter(model);
  const blank: BasicFilterCondition = { filterType, type: 'blank', colId };
  const notBlank: BasicFilterCondition = { filterType, type: 'notBlank', colId };
  switch (plan.kind) {
    case 'all':
      return null;
    case 'none':
      // Deselect all: an empty IN list matches no row.
      return { filterType, type: 'in', colId, values: [] };
    case 'include': {
      if (plan.values.length === 0) return blank;
      const inList: BasicFilterCondition = { filterType, type: 'in', colId, values: plan.values };
      return plan.includesNull ? { filterType: 'join', type: 'OR', conditions: [inList, blank] } : inList;
    }
    case 'exclude': {
      if (plan.values.length === 0) return notBlank;
      const notIn: BasicFilterCondition = { filterType, type: 'notIn', colId, values: plan.values };
      return plan.excludesNull
        ? { filterType: 'join', type: 'AND', conditions: [notIn, notBlank] }
        : { filterType: 'join', type: 'OR', conditions: [notIn, blank] };
    }
  }
}

export function isMultiFilterModel(filter: unknown): filter is GridMultiFilterModel {
  return Boolean(
    filter
      && typeof filter === 'object'
      && (filter as { filterType?: unknown }).filterType === 'multi'
      && Array.isArray((filter as { filterModels?: unknown }).filterModels),
  );
}

/**
 * AG Grid wraps child models from agMultiColumnFilter in a `multi` model. The
 * query layers operate on one condition at a time, so expand each active child
 * under a synthetic map key while retaining the real database column in colId.
 */
export function expandMultiFilterModel<T extends Record<string, any>>(filterModel: T): Record<string, any> {
  const expanded: Record<string, any> = {};

  Object.entries(filterModel ?? {}).forEach(([fieldName, filter]) => {
    if (!isMultiFilterModel(filter)) {
      expanded[fieldName] = filter;
      return;
    }

    filter.filterModels?.forEach((childFilter, index) => {
      if (!childFilter) return;
      expanded[`${fieldName}__multi_${index}`] = {
        ...childFilter,
        colId: typeof childFilter.colId === 'string' ? childFilter.colId : fieldName,
      };
    });
  });

  return expanded;
}
